/**
 * NewbuildProjectPage – Phase 2: Project detail with document upload & feasibility analysis
 * Redesigned with category-based checklist UI for document uploads.
 * Includes document link finder (hybrid: static + AI-generated links).
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../services/authService';

const API_BASE = import.meta.env.VITE_API_URL || '';
const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.webp,.tiff';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// Swiss cantons
const SWISS_CANTONS = [
  { code: 'AG', name: 'Aargau' },
  { code: 'AI', name: 'Appenzell Innerrhoden' },
  { code: 'AR', name: 'Appenzell Ausserrhoden' },
  { code: 'BE', name: 'Bern' },
  { code: 'BL', name: 'Basel-Landschaft' },
  { code: 'BS', name: 'Basel-Stadt' },
  { code: 'FR', name: 'Freiburg' },
  { code: 'GE', name: 'Genf' },
  { code: 'GL', name: 'Glarus' },
  { code: 'GR', name: 'Graubünden' },
  { code: 'JU', name: 'Jura' },
  { code: 'LU', name: 'Luzern' },
  { code: 'NE', name: 'Neuenburg' },
  { code: 'NW', name: 'Nidwalden' },
  { code: 'OW', name: 'Obwalden' },
  { code: 'SG', name: 'St. Gallen' },
  { code: 'SH', name: 'Schaffhausen' },
  { code: 'SO', name: 'Solothurn' },
  { code: 'SZ', name: 'Schwyz' },
  { code: 'TG', name: 'Thurgau' },
  { code: 'TI', name: 'Tessin' },
  { code: 'UR', name: 'Uri' },
  { code: 'VD', name: 'Waadt' },
  { code: 'VS', name: 'Wallis' },
  { code: 'ZG', name: 'Zug' },
  { code: 'ZH', name: 'Zürich' },
];

// ─── Custom Canton Dropdown ─────────────────────────────────────────
interface CantonDropdownProps {
  value: string;
  onChange: (code: string) => void;
}

function CantonDropdown({ value, onChange }: CantonDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = SWISS_CANTONS.find(c => c.code === value);

  const filtered = useMemo(() => {
    if (!search) return SWISS_CANTONS;
    const s = search.toLowerCase();
    return SWISS_CANTONS.filter(c =>
      c.code.toLowerCase().includes(s) || c.name.toLowerCase().includes(s)
    );
  }, [search]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(code: string) {
    onChange(code);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="nb-canton-dropdown" ref={ref}>
      <button
        type="button"
        className={`nb-canton-trigger ${open ? 'nb-canton-trigger--open' : ''}`}
        onClick={() => {
          setOpen(!open);
          if (!open) setTimeout(() => inputRef.current?.focus(), 50);
        }}
      >
        <span className={selected ? '' : 'nb-canton-placeholder'}>
          {selected ? `${selected.code} – ${selected.name}` : '– Kanton wählen –'}
        </span>
        <svg className={`nb-canton-chevron ${open ? 'nb-canton-chevron--open' : ''}`} viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div className="nb-canton-menu">
          <input
            ref={inputRef}
            type="text"
            className="nb-canton-search"
            placeholder="Suchen..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="nb-canton-list">
            {filtered.length === 0 && (
              <div className="nb-canton-empty">Kein Kanton gefunden</div>
            )}
            {filtered.map(c => (
              <button
                key={c.code}
                type="button"
                className={`nb-canton-option ${c.code === value ? 'nb-canton-option--active' : ''}`}
                onClick={() => handleSelect(c.code)}
              >
                <span className="nb-canton-code">{c.code}</span>
                <span>{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface HeroImageSettings {
  enabled: boolean;
  filename: string | null;
  opacity: number;
  blend_mode: string;
  blur: number;
}

interface Document {
  id: number;
  category: string;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  extraction_error?: string;
  created_at: string;
}

interface Category {
  key: string;
  label: string;
  group: string;
}

interface WishAnalysis {
  wish: string;
  status: 'possible' | 'limited' | 'not_possible';
  explanation: string;
  regulation_reference: string;
}

interface MaxPotential {
  max_gfa_m2: number | null;
  max_footprint_m2: number | null;
  max_floors: number | null;
  max_height_m: number | null;
  max_units_estimate: number | null;
  max_living_area_m2: number | null;
  notes: string;
}

interface Feasibility {
  wishes_analysis: WishAnalysis[];
  maximum_potential: MaxPotential;
  overall_feasibility: 'positive' | 'mixed' | 'critical';
  summary_de: string;
}

interface DocLink {
  label: string;
  url: string;
  description: string;
  source: 'static' | 'ai';
}

interface DocLinksCategory {
  category: string;
  links: DocLink[];
  fallback_text?: string;
}

interface DocLinksData {
  canton: string;
  municipality: string;
  generated_at: string;
  categories: DocLinksCategory[];
}

interface Project {
  id: number;
  name: string;
  status: string;
  municipality: string | null;
  plot_address: string | null;
  plot_zip: string | null;
  profile_json: any;
  profile_summary_de: string | null;
  feasibility_json: Feasibility | null;
  feasibility_summary_de: string | null;
  feasibility_updated_at: string | null;
  document_links_json: DocLinksData | null;
  documents: Document[];
  created_at: string;
  updated_at: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const statusIcons: Record<string, string> = {
  possible: '✅',
  limited: '⚠️',
  not_possible: '❌',
};

const statusLabels: Record<string, string> = {
  possible: 'Möglich',
  limited: 'Eingeschränkt',
  not_possible: 'Nicht möglich',
};

// ─── Category Upload Card ─────────────────────────────────────────────
interface CategoryCardProps {
  cat: Category;
  docs: Document[];
  projectId: string;
  onUploaded: () => void;
  onDeleteDoc: (docId: number) => void;
  links?: DocLinksCategory;
}

function CategoryCard({ cat, docs, projectId, onUploaded, onDeleteDoc, links }: CategoryCardProps) {
  const [expanded, setExpanded] = useState(docs.length > 0);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const hasFiles = docs.length > 0;
  const allExtracted = hasFiles && docs.every(d => d.extraction_status === 'completed');
  const someProcessing = docs.some(d => d.extraction_status === 'pending' || d.extraction_status === 'processing');

  async function uploadFiles(files: FileList | File[]) {
    const fileArray = Array.from(files);
    const valid = fileArray.filter(f => f.size <= MAX_FILE_SIZE);
    const oversized = fileArray.length - valid.length;

    if (valid.length === 0) {
      setUploadMsg(oversized > 0 ? 'Alle Dateien sind zu gross (max. 10 MB).' : 'Keine gültige Datei ausgewählt.');
      return;
    }

    setUploading(true);
    setUploadMsg(null);
    let successCount = 0;
    let failCount = 0;

    for (const file of valid) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', cat.key);
      try {
        const data = await apiRequest(`/api/newbuild-projects/${projectId}/documents`, {
          method: 'POST',
          body: formData,
        });
        if (data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    const msgs: string[] = [];
    if (successCount > 0) msgs.push(`${successCount} Datei(en) hochgeladen`);
    if (failCount > 0) msgs.push(`${failCount} fehlgeschlagen`);
    if (oversized > 0) msgs.push(`${oversized} zu gross`);
    setUploadMsg(msgs.join(' · '));
    setUploading(false);
    if (successCount > 0) onUploaded();
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    setDragging(true);
  }
  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  }
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation();
  }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      uploadFiles(e.target.files);
    }
  }

  return (
    <div className={`nb-cat-card ${expanded ? 'nb-cat-card--open' : ''} ${hasFiles ? 'nb-cat-card--has-files' : ''}`}>
      {/* Card header – always visible */}
      <button className="nb-cat-header" onClick={() => setExpanded(!expanded)}>
        <span className="nb-cat-check">
          {allExtracted ? (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          ) : someProcessing ? (
            <span className="spinner spinner--xs" />
          ) : hasFiles ? (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          ) : (
            <span className="nb-cat-circle" />
          )}
        </span>
        <span className="nb-cat-label">{cat.label}</span>
        {hasFiles && <span className="nb-cat-badge">{docs.length}</span>}
        <svg className={`nb-cat-chevron ${expanded ? 'nb-cat-chevron--open' : ''}`} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {/* Document links – always visible below header */}
      {links && links.links.length > 0 && (
        <div className="nb-cat-links">
          <span className="nb-cat-links-label">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Wo finde ich das?
          </span>
          {links.links.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="nb-cat-link-btn" title={link.description}>
              {link.label}
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 17 17 7"/><polyline points="7 7 17 7 17 17"/></svg>
            </a>
          ))}
        </div>
      )}
      {links && links.links.length === 0 && links.fallback_text && (
        <div className="nb-cat-links nb-cat-links--fallback">
          <span className="nb-cat-links-label">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            {links.fallback_text}
          </span>
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="nb-cat-body">
          {/* Uploaded files for this category */}
          {docs.length > 0 && (
            <div className="nb-cat-files">
              {docs.map(doc => (
                <div key={doc.id} className="nb-doc-item">
                  <div className="nb-doc-icon">
                    {doc.mime_type === 'application/pdf' ? (
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    )}
                  </div>
                  <div className="nb-doc-info">
                    <span className="nb-doc-name">{doc.original_filename}</span>
                    <span className="nb-doc-meta">{formatBytes(doc.file_size_bytes)}</span>
                  </div>
                  <div className="nb-doc-status">
                    {doc.extraction_status === 'completed' && (
                      <span className="nb-extraction-badge nb-extraction--done" title="Textextraktion abgeschlossen">✓ Extrahiert</span>
                    )}
                    {doc.extraction_status === 'processing' && (
                      <span className="nb-extraction-badge nb-extraction--processing" title="Textextraktion läuft...">
                        <span className="spinner spinner--xs" /> Verarbeitung...
                      </span>
                    )}
                    {doc.extraction_status === 'pending' && (
                      <span className="nb-extraction-badge nb-extraction--pending" title="Wartet auf Extraktion">Wartend</span>
                    )}
                    {doc.extraction_status === 'failed' && (
                      <span className="nb-extraction-badge nb-extraction--failed" title={doc.extraction_error || 'Fehler'}>✗ Fehler</span>
                    )}
                  </div>
                  <button className="nb-doc-delete" onClick={() => onDeleteDoc(doc.id)} title="Löschen">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Drop zone */}
          <div
            className={`nb-dropzone ${dragging ? 'nb-dropzone--active' : ''} ${uploading ? 'nb-dropzone--uploading' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_TYPES}
              multiple
              className="nb-dropzone-input"
              onChange={handleFileChange}
            />
            {uploading ? (
              <div className="nb-dropzone-content">
                <span className="spinner spinner--sm" />
                <span>Hochladen...</span>
              </div>
            ) : (
              <div className="nb-dropzone-content">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>{hasFiles ? 'Weitere Dateien hochladen' : 'Dateien hierher ziehen oder klicken'}</span>
                <small>PDF, JPG, PNG, WebP, TIFF – max. 10 MB</small>
              </div>
            )}
          </div>

          {uploadMsg && <p className="nb-cat-upload-msg">{uploadMsg}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────
export default function NewbuildProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Document links state
  const [docLinks, setDocLinks] = useState<DocLinksData | null>(null);
  const [linksCanton, setLinksCanton] = useState('');
  const [linksMunicipality, setLinksMunicipality] = useState('');
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState<string | null>(null);

  // Background image
  const [heroImage, setHeroImage] = useState<HeroImageSettings | null>(null);

  // Polling for extraction status
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch background image (same as Newbuild profile page)
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/newbuild-profile-image-settings`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setHeroImage(data.data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/#lab-auth');
      return;
    }
    loadProject();
    loadCategories();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id, isAuthenticated]);

  // Poll for extraction status when documents are pending/processing
  useEffect(() => {
    if (!project) return;
    const hasPending = project.documents.some(d => d.extraction_status === 'pending' || d.extraction_status === 'processing');
    if (hasPending) {
      pollRef.current = setInterval(() => loadProject(), 5000);
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [project?.documents]);

  // Track if auto-detect already ran to avoid re-running on every loadProject
  const autoDetectRan = useRef(false);

  const loadProject = useCallback(async () => {
    try {
      const data = await apiRequest(`/api/newbuild-projects/${id}`);
      if (data.success) {
        setProject(data.data);
        // Restore cached document links
        if (data.data.document_links_json) {
          setDocLinks(data.data.document_links_json);
          if (!linksCanton && data.data.document_links_json.canton) setLinksCanton(data.data.document_links_json.canton);
          if (!linksMunicipality && data.data.document_links_json.municipality) setLinksMunicipality(data.data.document_links_json.municipality);
        }
        // Auto-fill location from profile if not yet set
        const muni = data.data.municipality;
        if (!linksMunicipality && muni) {
          setLinksMunicipality(muni);
        }
        // Auto-detect canton from municipality (only once)
        if (!autoDetectRan.current && !linksCanton && muni && !data.data.document_links_json?.canton) {
          autoDetectRan.current = true;
          autoDetectCanton(muni);
        }
      } else {
        setError(data.error?.message || 'Projekt nicht gefunden');
      }
    } catch {
      setError('Fehler beim Laden des Projekts');
    }
    setLoading(false);
  }, [id]);

  async function autoDetectCanton(municipality: string) {
    try {
      const res = await fetch(`${API_BASE}/api/newbuild-projects/lookup/canton?municipality=${encodeURIComponent(municipality)}`);
      const data = await res.json();
      if (data.success && data.data?.canton) {
        setLinksCanton(data.data.canton);
      }
    } catch { /* ignore */ }
  }

  async function loadCategories() {
    try {
      const res = await fetch(`${API_BASE}/api/newbuild-projects/meta/categories`);
      const data = await res.json();
      if (data.success) setCategories(data.data);
    } catch { /* ignore */ }
  }

  async function handleDeleteDoc(docId: number) {
    if (!confirm('Dokument wirklich löschen?')) return;
    try {
      await apiRequest(`/api/newbuild-projects/${id}/documents/${docId}`, { method: 'DELETE' });
      loadProject();
    } catch { /* ignore */ }
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const data = await apiRequest(`/api/newbuild-projects/${id}/analyze`, { method: 'POST' });
      if (data.success) {
        loadProject();
      } else {
        setAnalysisError(data.error?.message || 'Analyse fehlgeschlagen');
      }
    } catch (err: any) {
      setAnalysisError('Netzwerkfehler: ' + err.message);
    }
    setAnalyzing(false);
  }

  async function handleDeleteProject() {
    if (!confirm('Projekt und alle Dokumente wirklich löschen?')) return;
    try {
      await apiRequest(`/api/newbuild-projects/${id}`, { method: 'DELETE' });
      navigate('/newbuild/profile');
    } catch { /* ignore */ }
  }

  async function handleGenerateLinks() {
    if (!linksCanton || !linksMunicipality) {
      setLinksError('Bitte wählen Sie einen Kanton und geben Sie die Gemeinde ein.');
      return;
    }
    setLinksLoading(true);
    setLinksError(null);
    try {
      const data = await apiRequest(`/api/newbuild-projects/${id}/document-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canton: linksCanton, municipality: linksMunicipality }),
      });
      if (data.success) {
        setDocLinks(data.data);
      } else {
        setLinksError(data.error?.message || 'Linksuche fehlgeschlagen');
      }
    } catch (err: any) {
      setLinksError('Netzwerkfehler: ' + err.message);
    }
    setLinksLoading(false);
  }

  // Map document links by category key for easy lookup
  const linksByCategory = useMemo(() => {
    if (!docLinks?.categories) return {};
    const map: Record<string, DocLinksCategory> = {};
    for (const cat of docLinks.categories) {
      map[cat.category] = cat;
    }
    return map;
  }, [docLinks]);

  if (loading) {
    return (
      <div className="newbuild-project-page newbuild-page">
        <div className="hero-orb hero-orb--1" style={{ position: 'fixed' }} />
        <div className="hero-orb hero-orb--2" style={{ position: 'fixed' }} />
        {heroImage && heroImage.enabled && heroImage.filename && (
          <div className="hero-bg-image" style={{ position: 'fixed', backgroundImage: `url(/uploads/${heroImage.filename})`, opacity: heroImage.opacity / 100, mixBlendMode: heroImage.blend_mode as any, filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined }} />
        )}
        <div className="nb-project-loading">
          <div className="spinner" /> Projekt wird geladen...
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="newbuild-project-page newbuild-page">
        <div className="hero-orb hero-orb--1" style={{ position: 'fixed' }} />
        <div className="hero-orb hero-orb--2" style={{ position: 'fixed' }} />
        {heroImage && heroImage.enabled && heroImage.filename && (
          <div className="hero-bg-image" style={{ position: 'fixed', backgroundImage: `url(/uploads/${heroImage.filename})`, opacity: heroImage.opacity / 100, mixBlendMode: heroImage.blend_mode as any, filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined }} />
        )}
        <div className="nb-project-error">
          <p>{error || 'Projekt nicht gefunden'}</p>
          <button className="btn btn--primary" onClick={() => navigate('/newbuild/profile')}>Zurück</button>
        </div>
      </div>
    );
  }

  const completedDocs = project.documents.filter(d => d.extraction_status === 'completed');
  const pendingDocs = project.documents.filter(d => d.extraction_status === 'pending' || d.extraction_status === 'processing');
  const canAnalyze = completedDocs.length > 0 && !!project.profile_json;

  // Group categories for display
  const groupedCategories = categories.reduce<Record<string, Category[]>>((acc, c) => {
    if (!acc[c.group]) acc[c.group] = [];
    acc[c.group].push(c);
    return acc;
  }, {});

  // Map documents by category
  const docsByCategory = project.documents.reduce<Record<string, Document[]>>((acc, d) => {
    if (!acc[d.category]) acc[d.category] = [];
    acc[d.category].push(d);
    return acc;
  }, {});

  // Progress stats
  const totalCategories = categories.filter(c => c.key !== 'other').length;
  const filledCategories = categories.filter(c => c.key !== 'other' && (docsByCategory[c.key]?.length || 0) > 0).length;

  return (
    <div className="newbuild-project-page newbuild-page">
      {/* ── Background (fixed fullscreen) ── */}
      <div className="hero-orb hero-orb--1" style={{ position: 'fixed' }} />
      <div className="hero-orb hero-orb--2" style={{ position: 'fixed' }} />
      <div className="hero-orb hero-orb--3" style={{ position: 'fixed' }} />
      {heroImage && heroImage.enabled && heroImage.filename && (
        <div
          className="hero-bg-image"
          style={{
            position: 'fixed',
            backgroundImage: `url(/uploads/${heroImage.filename})`,
            opacity: heroImage.opacity / 100,
            mixBlendMode: heroImage.blend_mode as any,
            filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined,
          }}
        />
      )}

      {/* Header */}
      <div className="nb-project-header">
        <div className="nb-project-header-left">
          <button className="nb-back-btn" onClick={() => navigate('/newbuild/profile')} title="Zurück">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="nb-project-title">{project.name}</h1>
            <span className="nb-project-meta">
              {project.municipality && <span>{project.municipality}</span>}
              {project.plot_address && <span> · {project.plot_address}</span>}
              {project.plot_zip && <span> · {project.plot_zip}</span>}
            </span>
          </div>
        </div>
        <div className="nb-project-header-right">
          <span className={`nb-status nb-status--${project.status}`}>
            {project.status === 'draft' && 'Entwurf'}
            {project.status === 'documents_uploaded' && 'Dokumente hochgeladen'}
            {project.status === 'analysis_complete' && 'Analyse abgeschlossen'}
          </span>
          <button className="btn btn--sm btn--danger" onClick={handleDeleteProject} title="Projekt löschen">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          </button>
        </div>
      </div>

      {/* Profile summary */}
      {project.profile_summary_de && (
        <div className="nb-section nb-profile-summary">
          <h2>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Neubauprofil
          </h2>
          <p>{project.profile_summary_de}</p>
        </div>
      )}

      {!project.profile_json && (
        <div className="nb-section nb-profile-missing">
          <p>Noch kein Neubauprofil vorhanden. Bitte zuerst das <a href="/newbuild">Chat-Interview</a> durchführen.</p>
        </div>
      )}

      {/* ── Document Link Finder ──────────────────────────────── */}
      <div className="nb-section nb-link-finder">
        <h2>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          Dokumente finden
        </h2>
        <p className="nb-link-finder-desc">
          Wählen Sie Ihren Kanton und Ihre Gemeinde, um direkte Links zu den relevanten Planungsdokumenten zu erhalten.
        </p>

        <div className="nb-link-finder-form">
          <div className="nb-link-finder-field">
            <label>Kanton</label>
            <CantonDropdown value={linksCanton} onChange={setLinksCanton} />
          </div>
          <div className="nb-link-finder-field">
            <label htmlFor="nb-link-municipality">Gemeinde</label>
            <input
              id="nb-link-municipality"
              type="text"
              value={linksMunicipality}
              onChange={e => setLinksMunicipality(e.target.value)}
              placeholder="z.B. Winterthur"
              className="nb-link-input"
            />
          </div>
          <button
            className="btn btn--primary nb-link-finder-btn"
            onClick={handleGenerateLinks}
            disabled={linksLoading || !linksCanton || !linksMunicipality}
          >
            {linksLoading ? (
              <><span className="spinner spinner--xs" /> Suche läuft...</>
            ) : docLinks ? (
              <>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                Erneut suchen
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                Links suchen
              </>
            )}
          </button>
        </div>

        {linksError && <p className="nb-link-finder-error">{linksError}</p>}

        {docLinks && (
          <p className="nb-link-finder-info">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Links für <strong>{docLinks.municipality}</strong> ({docLinks.canton}) gefunden – sie erscheinen bei jeder Kategorie unten.
          </p>
        )}
      </div>

      {/* ── Document Checklist ───────────────────────────────── */}
      <div className="nb-section nb-documents">
        <h2>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Dokumente hochladen
        </h2>

        {/* Progress bar */}
        <div className="nb-progress">
          <div className="nb-progress-bar">
            <div
              className="nb-progress-fill"
              style={{ width: totalCategories > 0 ? `${(filledCategories / totalCategories) * 100}%` : '0%' }}
            />
          </div>
          <span className="nb-progress-text">
            {filledCategories} von {totalCategories} Kategorien ausgefüllt · {project.documents.length} Datei{project.documents.length !== 1 ? 'en' : ''} hochgeladen
          </span>
        </div>

        {/* Category groups */}
        {Object.entries(groupedCategories).map(([group, cats]) => (
          <div key={group} className="nb-cat-group">
            <h3 className="nb-cat-group-title">{group}</h3>
            <div className="nb-cat-list">
              {cats.map(cat => (
                <CategoryCard
                  key={cat.key}
                  cat={cat}
                  docs={docsByCategory[cat.key] || []}
                  projectId={id!}
                  onUploaded={loadProject}
                  onDeleteDoc={handleDeleteDoc}
                  links={linksByCategory[cat.key]}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Analysis section */}
      <div className="nb-section nb-analysis">
        <h2>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          Machbarkeitsanalyse
        </h2>

        {pendingDocs.length > 0 && (
          <p className="nb-analysis-hint">
            <span className="spinner spinner--xs" /> {pendingDocs.length} Dokument(e) werden noch verarbeitet. Bitte warten Sie, bis die Extraktion abgeschlossen ist.
          </p>
        )}

        <div className="nb-analysis-actions">
          <button
            className="btn btn--primary"
            onClick={handleAnalyze}
            disabled={!canAnalyze || analyzing || pendingDocs.length > 0}
          >
            {analyzing ? (
              <><span className="spinner spinner--xs" /> Analyse läuft...</>
            ) : project.feasibility_json ? (
              'Analyse erneut durchführen'
            ) : (
              'Machbarkeit prüfen'
            )}
          </button>

          <button
            className="btn btn--outline nb-later-btn"
            onClick={() => navigate('/newbuild/profile')}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Dokumente später hinzufügen
          </button>
        </div>

        {!canAnalyze && !project.feasibility_json && (
          <p className="nb-analysis-hint" style={{ marginTop: '0.5rem' }}>
            {!project.profile_json ? 'Benötigt: Neubauprofil (Chat-Interview)' : ''}
            {!project.profile_json && completedDocs.length === 0 ? ' + ' : ''}
            {completedDocs.length === 0 ? 'Benötigt: Mindestens ein analysiertes Dokument' : ''}
          </p>
        )}

        {analysisError && (
          <p className="nb-analysis-error">{analysisError}</p>
        )}

        {/* Feasibility Results */}
        {project.feasibility_json && (
          <div className="nb-feasibility">
            {/* Overall summary */}
            <div className={`nb-feasibility-summary nb-feasibility--${project.feasibility_json.overall_feasibility}`}>
              <div className="nb-feasibility-icon">
                {project.feasibility_json.overall_feasibility === 'positive' && '✅'}
                {project.feasibility_json.overall_feasibility === 'mixed' && '⚠️'}
                {project.feasibility_json.overall_feasibility === 'critical' && '❌'}
              </div>
              <div>
                <strong>
                  {project.feasibility_json.overall_feasibility === 'positive' && 'Positiv'}
                  {project.feasibility_json.overall_feasibility === 'mixed' && 'Teilweise möglich'}
                  {project.feasibility_json.overall_feasibility === 'critical' && 'Kritisch'}
                </strong>
                <p>{project.feasibility_json.summary_de}</p>
              </div>
            </div>

            {/* Part 1: Wishes analysis */}
            <h3 className="nb-feasibility-h3">Teil 1: Ihre Wünsche</h3>
            <div className="nb-wishes-list">
              {project.feasibility_json.wishes_analysis.map((w, i) => (
                <div key={i} className={`nb-wish-item nb-wish--${w.status}`}>
                  <span className="nb-wish-icon">{statusIcons[w.status]}</span>
                  <div className="nb-wish-content">
                    <strong>{w.wish}</strong>
                    <span className="nb-wish-status">{statusLabels[w.status]}</span>
                    <p>{w.explanation}</p>
                    {w.regulation_reference && (
                      <small className="nb-wish-ref">{w.regulation_reference}</small>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Part 2: Maximum potential */}
            <h3 className="nb-feasibility-h3">Teil 2: Maximales Baupotenzial</h3>
            <div className="nb-potential-grid">
              {project.feasibility_json.maximum_potential.max_gfa_m2 != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_gfa_m2.toLocaleString('de-CH')} m²</span>
                  <span className="nb-potential-label">Max. Geschossfläche</span>
                </div>
              )}
              {project.feasibility_json.maximum_potential.max_footprint_m2 != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_footprint_m2.toLocaleString('de-CH')} m²</span>
                  <span className="nb-potential-label">Max. Grundfläche</span>
                </div>
              )}
              {project.feasibility_json.maximum_potential.max_floors != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_floors}</span>
                  <span className="nb-potential-label">Max. Geschosse</span>
                </div>
              )}
              {project.feasibility_json.maximum_potential.max_height_m != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_height_m} m</span>
                  <span className="nb-potential-label">Max. Höhe</span>
                </div>
              )}
              {project.feasibility_json.maximum_potential.max_units_estimate != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_units_estimate}</span>
                  <span className="nb-potential-label">Max. Wohneinheiten</span>
                </div>
              )}
              {project.feasibility_json.maximum_potential.max_living_area_m2 != null && (
                <div className="nb-potential-card">
                  <span className="nb-potential-value">{project.feasibility_json.maximum_potential.max_living_area_m2.toLocaleString('de-CH')} m²</span>
                  <span className="nb-potential-label">Max. Wohnfläche</span>
                </div>
              )}
            </div>
            {project.feasibility_json.maximum_potential.notes && (
              <p className="nb-potential-notes">{project.feasibility_json.maximum_potential.notes}</p>
            )}

            {project.feasibility_updated_at && (
              <small className="nb-feasibility-date">
                Analyse vom {new Date(project.feasibility_updated_at).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
